import React, { useEffect, useState } from 'react';
import moment from 'moment';

import CallIcon from '../../assets/icons/CallIcon';
import MeetingIcon from '../../assets/icons/MeetingIcon';
import TrainingIcon from '../../assets/icons/TrainingIcon';
import DemoIcon from '../../assets/icons/DemoIcon';

import ColorButton from '../shared/ColorButton';
import TextInput from '../shared/TextInput';
import DatePicker from '../shared/date-picker/NewDatePicker';
import TimePicker from '../shared/time-picker/NewTimePicker';
import SelectBox from '../shared/select/SelectBox';

import { leadActivityCategories, leadActivityDurationOptions } from '../../constants/sales';

import './leads.scss';

const durationOptions = Object.values(leadActivityDurationOptions);

const getDefaultDuration = (category) => {
  if (category === leadActivityCategories.MEETING.value) {
    return leadActivityDurationOptions['1_HOUR'].value;
  }

  if ([leadActivityCategories.DEMO.value, leadActivityCategories.TRAINING.value].includes(category)) {
    return leadActivityDurationOptions.HALF_DAY.value;
  }

  return leadActivityDurationOptions['10_MINUTES'].value;
};

const getCategoryLabel = (category) => leadActivityCategories[category]?.label || '';

const LeadActivityForm = ({ onCancel, onSubmit, loading, initialValue = null, submitText = 'Submit' }) => {
  const [activeTab, setActiveTab] = useState(initialValue?.category || leadActivityCategories.CALL.value);

  const [title, setTitle] = useState(initialValue?.title || getCategoryLabel(initialValue?.category || leadActivityCategories.CALL.value));
  const [date, setDate] = useState(initialValue?.bookingData?.date ? moment(initialValue.bookingData.date) : moment());
  const [time, setTime] = useState(initialValue?.bookingData?.date ? moment(initialValue.bookingData.date) : moment());
  const [duration, setDuration] = useState(initialValue?.bookingData?.duration || getDefaultDuration(initialValue?.category || leadActivityCategories.CALL.value));
  const [payload, setPayload] = useState(initialValue?.payload || '');

  useEffect(() => {
    if (initialValue) {
      const nextCategory = initialValue.category || leadActivityCategories.CALL.value;
      const nextDate = initialValue?.bookingData?.date ? moment(initialValue.bookingData.date) : moment();

      setActiveTab(nextCategory);
      setTitle(initialValue?.title || getCategoryLabel(nextCategory));
      setDate(nextDate);
      setTime(nextDate);
      setDuration(initialValue?.bookingData?.duration || getDefaultDuration(nextCategory));
      setPayload(initialValue?.payload || '');
    } else {
      setActiveTab(leadActivityCategories.CALL.value);
      setTitle(getCategoryLabel(leadActivityCategories.CALL.value));
      setDate(moment());
      setTime(moment());
      setDuration(getDefaultDuration(leadActivityCategories.CALL.value));
      setPayload('');
    }
  }, [initialValue]);

  const handleCategoryChange = (nextCategory) => {
    setActiveTab(nextCategory);
    setTitle(getCategoryLabel(nextCategory));
    setDuration(getDefaultDuration(nextCategory));
  };

  const handleSubmit = () => {
    const mergedDate = moment(date)
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second())
      .millisecond(time.millisecond());

    const formObj = {
      category: activeTab,
      date: mergedDate,
      title,
      time,
      duration,
      payload,
    };
    onSubmit(formObj);
  };

  return (
    <div className="m-t-lg">
      <div className='d-flex'>
        <div className='m-r-md'>
          <ColorButton
            color={activeTab === leadActivityCategories.CALL.value ? 'blue' : 'grey'}
            onClick={() => handleCategoryChange(leadActivityCategories.CALL.value)}
            text={<div className='d-flex'> <CallIcon fontSize='small' /> <span className='m-l-md'>{leadActivityCategories.CALL.label}</span></div>}
          />
        </div>
        <div className='m-l-md m-r-md'>
          <ColorButton
            color={activeTab === leadActivityCategories.MEETING.value ? 'blue' : 'grey'}
            onClick={() => handleCategoryChange(leadActivityCategories.MEETING.value)}
            text={<div className='d-flex'> <MeetingIcon fontSize='small' /> <span className='m-l-md'>{leadActivityCategories.MEETING.label}</span></div>}
          />
        </div>
        <div className='m-l-md m-r-md'>
          <ColorButton
            color={activeTab === leadActivityCategories.TRAINING.value ? 'blue' : 'grey'}
            onClick={() => handleCategoryChange(leadActivityCategories.TRAINING.value)}
            text={<div className='d-flex'> <TrainingIcon fontSize='small' /> <span className='m-l-md'>{leadActivityCategories.TRAINING.label}</span></div>}
          />
        </div>
        <div className='m-l-md'>
          <ColorButton
            color={activeTab === leadActivityCategories.DEMO.value ? 'blue' : 'grey'}
            onClick={() => handleCategoryChange(leadActivityCategories.DEMO.value)}
            text={<div className='d-flex'> <DemoIcon fontSize='small' /> <span className='m-l-md'>{leadActivityCategories.DEMO.label}</span></div>}
          />
        </div>
      </div>

      <div className="m-t-lg d-flex">
        <div className="m-r-md flex-1">
          <div className="secondary m-b-sm">Title</div>
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="m-l-md m-r-md">
          <div className="secondary m-b-sm">Date</div>
          <DatePicker
            date={date}
            onDateChange={setDate}
          />
        </div>

        <div className="m-l-md m-r-md">
          <div className="secondary m-b-sm">Time</div>
          <TimePicker
            value={time}
            onChange={setTime}
            views={['hours', 'minutes']}
          />
        </div>

        <div className="m-l-md" style={{ width: '150px' }}>
          <div className="secondary m-b-sm">Duration</div>
          <SelectBox
            value={duration}
            onChange={setDuration}
            options={durationOptions}
          />
        </div>
      </div>

      <div className="m-t-lg">
        <div className="secondary m-b-sm">Details</div>
        <TextInput
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          multiline
          rows={4}
          placeholder="Add any additional details here"
        />
      </div>

      <div className='m-t-lg d-flex flex-end'>
        <div className='d-flex'>
          <div className='m-r-md'>
            <ColorButton
              color='grey'
              onClick={onCancel}
              text='Cancel'
            />
          </div>
          <div className='m-l-md'>
            <ColorButton
              color='blue'
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
              text={submitText}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadActivityForm;
